import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";
import { filterBranches } from "@base/modules/doctor/doctorProfile";
import { ISiteBranch } from "@base/modules/site/site.interface";
import CommonStyles from "@components/CommonStyles";

interface IProps {
  open: boolean;
  toggle: () => void;
  branches: ISiteBranch[];
  onSelect: (branch: ISiteBranch) => void;
}

/** Which of the site's branches to book at. Asked even when there is only one, so the patient sees where. */
export default function BranchPickerModal({ open, toggle, branches, onSelect }: IProps) {
  //! state
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState<string>("");
  const matched = useMemo(() => filterBranches(branches, keyword), [branches, keyword]);

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      width={640}
      title={t("doctorDetail.selectBranch")}
      content={
        <div className="flex flex-col gap-3">
          <CommonStyles.InputUI
            variant="filled"
            allowClear
            value={keyword}
            placeholder={t("doctorDetail.searchBranch")}
            prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
          />

          <div className="flex flex-col gap-2 max-h-[calc(100vh-320px)] overflow-y-auto">
            {matched.map((branch, index) => (
              <button
                type="button"
                key={branch.code ?? index}
                onClick={() => onSelect(branch)}
                className="flex flex-col gap-0.5 p-3 rounded-lg border border-common-divider bg-common-white text-left cursor-pointer hover:border-common-primary hover:bg-common-primary50 transition-colors"
              >
                <span className="text-sm font-medium font-['Inter'] text-common-maintext">{branch.name}</span>
                {branch.description ? <span className="text-xs font-['Inter'] text-common-subtext">{branch.description}</span> : null}
              </button>
            ))}

            {!matched.length ? (
              <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                {t("noResult")}
              </div>
            ) : null}
          </div>
        </div>
      }
    />
  );
}
